import React, { useState, useEffect } from "react";
import "../JobPosting/jobs.scss";
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Snackbar } from "@mui/material";
import { fetchJobById,} from "../../Redux/ActionCreators/jobsAction";
import { fetchQualifyingQuestions, fetchQualifyingQuestionById, saveQualifyingQuestion, updateQualifyingQuestion, deleteQualifyingQuestion } from "../../Redux/ActionCreators/jobsAction";
import { updateHiringAssistaniData } from "../../Redux/ActionCreators/qualifyQuestionsAction";

const PreScreening = ({ setType }) => {
  const { jobId } = useParams();
  const dispatch = useDispatch();

  const questions = useSelector((state) => state.jobs.qualifyingQuestions || []);
  const job = useSelector((state) => state.jobs.latestJob);

  const [newQuestion, setNewQuestion] = useState({
    question: "",
    type: "",
    score: "",
    choices: ["", ""],
    correctAnswer: "",
    is_required: false,
  });

  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passingScore, setPassingScore] = useState(0);
  const [originalScore, setOriginalScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const loadQuestions = async () => {
    setLoading(true);
    await dispatch(fetchQualifyingQuestions(jobId));
    setLoading(false);
  };

  useEffect(() => {
    if (jobId) {
      dispatch(fetchJobById(jobId));
    }
  }, [jobId]);

  useEffect(() => {
    loadQuestions();
  }, [jobId, dispatch]);

  useEffect(() => {
    if (job?.hiring_stages) {
      const stage = job.hiring_stages.find(
        (s) => s.hiring_stage_id === "fd40e3d2-38a9-4cef-937a-93ef6a42d045"
      );
      const score = stage?.data?.passing_score ?? 0;
      setPassingScore(score);
      setOriginalScore(score);
    }
  }, [job]);

  useEffect(() => {
    if (questions.length > 0) {
      const total = questions.reduce((sum, q) => sum + (parseInt(q.score, 10) || 0), 0);
      setTotalScore(total);
    } else {
      setTotalScore(0);
    }
  }, [questions]);

  const resetForm = () => {
    setNewQuestion({
      question: "",
      type: "",
      score: "",
      choices: ["", ""],
      correctAnswer: "",
      is_required: false,
    });
    setEditIndex(null);
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddChoice = () => {
    if (newQuestion.choices.length < 4) {
      setNewQuestion({
        ...newQuestion,
        choices: [...newQuestion.choices, ""],
      });
    }
  };

  const handleChoiceChange = (value, index) => {
    const updated = [...newQuestion.choices];
    if (typeof updated[index] === "object") {
      updated[index] = { ...updated[index], option_text: value };
    } else {
      updated[index] = { option_text: value };
    }
    setNewQuestion({ ...newQuestion, choices: updated });
  };

  const handleEdit = async (id) => {
    setLoading(true);
    const data = await dispatch(fetchQualifyingQuestionById(jobId, id));
    setLoading(false);
    if (!data) return;

    const questionType = Object.keys(QUESTION_TYPE_IDS).find(
      key => QUESTION_TYPE_IDS[key] === data.question_type_id
    );

    const mappedQuestion = {
      question: data.question_text,
      type: questionType,
      score: data.score,
      choices: data.options?.map(opt => ({ id: opt.id, option_text: opt.option_text })) || [{ option_text: "" }, { option_text: "" }],
      correctAnswer:
        questionType === "Numeric"
          ? data.meta_data?.required_answer_text || ""
          : data.meta_data?.required_option_text || "",
      is_required: data.is_required,
    };

    setNewQuestion(mappedQuestion);
    setEditIndex(questions.findIndex(q => q.id === id));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    const success = await dispatch(deleteQualifyingQuestion(jobId, id));
    if (success) {
      await loadQuestions(jobId);
      showSnackbar("Question deleted successfully", "info");
    }
    setLoading(false);
  };

  const QUESTION_TYPE_IDS = {
    "MCQ": "4c524f2f-48de-493e-894f-4f0bd931b4c9",
    "Numeric": "64468292-d741-4e52-893f-0846dd56830d",
    "Yes/No": "4ffc711b-2f10-470b-b162-85f2a2f1e220",
    "Free Text": "30afb159-85d7-490d-afcd-1af9497531ae",
  };

  const QUESTION_TYPE_ID_TO_LABEL = {
    "4c524f2f-48de-493e-894f-4f0bd931b4c9": "MCQ",
    "64468292-d741-4e52-893f-0846dd56830d": "Numeric",
    "4ffc711b-2f10-470b-b162-85f2a2f1e220": "Yes/No",
    "30afb159-85d7-490d-afcd-1af9497531ae": "Free Text",
  };

  const buildQuestionPayload = (question) => {
    let meta_data = {};
    if (question.type === "Numeric") {
      meta_data = { required_answer_text: question.correctAnswer };
    } else if (question.type === "Free Text") {
      meta_data = { required_option_text: question.correctAnswer };
    } else {
      meta_data = { required_option_text: question.correctAnswer };
    }


    const options =
      question.type === "MCQ"
        ? question.choices.filter(Boolean).map(opt =>
            typeof opt === "object"
              ? { id: opt.id, option_text: opt.option_text }
              : { option_text: opt }
          )
        : question.type === "Yes/No"
        ? ["Yes", "No"].map(text => ({ option_text: text }))
        : [];


    return {
      question_text: question.question,
      display_order: 0,
      is_required: !!question.is_required,
      is_active: true,
      score: parseInt(question.score, 10) || 0,
      question_type_id: QUESTION_TYPE_IDS[question.type],
      meta_data,
      options
    };
  };

  const validateAndSave = async () => {
    if (!newQuestion.type) {
      showSnackbar("You have to select any one of the question types!", "error");
      return;
    }
    if (!newQuestion.question.trim()) {
      showSnackbar("Question field is mandatory!", "error");
      return;
    }
    if (!newQuestion.score) {
      showSnackbar("Score field is mandatory!", "error");
      return;
    }
    if (newQuestion.type === "Free Text" && newQuestion.correctAnswer.length > 50) {
      showSnackbar("Free text answer should be under 50 characters!", "error");
      return;
    }
    if (!newQuestion.correctAnswer?.trim()) {
      if (
        newQuestion.type === "MCQ" ||
        newQuestion.type === "Numeric" ||
        newQuestion.type === "Yes/No" ||
        newQuestion.type === "Free Text"
      ) {
        showSnackbar("Suggested / Correct Answer is mandatory!", "error");
        return;
      }
    }

    setSaveLoading(true);
    const payload = buildQuestionPayload(newQuestion);

    const success = editIndex !== null
      ? await dispatch(updateQualifyingQuestion(jobId, questions[editIndex].id, payload))
      : await dispatch(saveQualifyingQuestion(jobId, payload));

    if (success) {
      await loadQuestions(jobId);
      resetForm();
      setShowForm(false);
      showSnackbar("Question saved successfully!", "success");
    }

    setSaveLoading(false);
  };

  const handleBack = () => {
    if (showForm) {
      resetForm();
      setShowForm(false);
      loadQuestions();
    } else {
      setType("");
    }
  }

  const handleSavePassingScore = async () => {
  try {
    const stage = job?.hiring_stages?.find(
      (s) => s.hiring_stage_id === "fd40e3d2-38a9-4cef-937a-93ef6a42d045"
    );
    if (!stage?.id) return;

    setSaveLoading(true);
    const payload = { data: { passing_score: passingScore } };

    const result = await dispatch(
        updateHiringAssistaniData(job.id, stage.id, payload)
      );

      if (result?.success !== false) {
        showSnackbar("Passing score updated successfully!", "success");
        setOriginalScore(passingScore);
        dispatch(fetchJobById(jobId)); // refresh
      } else {
        showSnackbar(result?.message || "Failed to update passing score");
      }
    } catch (err) {
      showSnackbar("Error while saving passing score");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="job-prescreening">
      <div className="job-settings-head">
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => {handleBack()}}
          style={{ cursor: "pointer" }}
        />
        <div className="job-settings-head-title">
          <h3>Pre-Screening</h3>
          <p>{questions.length} questions, Passing score: 
            {job?.hiring_stages?.find((stage) => stage.hiring_stage_id === "fd40e3d2-38a9-4cef-937a-93ef6a42d045")?.data?.passing_score ?? "N/A"}</p>
        </div>
      </div>

      {/* Show questions list if not in form */}
      {!showForm && (
        <div className="job-settings-body">
          {loading ? (
          <div
            className="loading-overlay"
            style={{
              position: "relative",
              marginTop: 16,
              alignItems: "start",
              height:'32px'
            }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
            <>
              {questions.length === 0 ? (
                <p style={{ marginTop: "14px", color: "#666", marginBottom:'20px' }}>
                  There are no Pre-Screening questions. You can add by clicking <b>Add question</b>.
                </p>
              ) : (
                questions.map((q, index) => (
                  <div className="job-settings-body-item" key={index}>
                    <p>{q.question_text}</p>
                    <h6>{QUESTION_TYPE_ID_TO_LABEL[q.question_type_id] || "Unknown"}</h6>
                    <h6>Score: {q.score}</h6>
                    <div className="job-settings-body-icons">
                      <button onClick={() => handleEdit(q.id)}>
                        <img className="edit-icon" src="/images/square-pen-black.svg" alt="edit" width={24} height={24}/>
                      </button>
                      <button onClick={() => handleDelete(q.id)}>
                        <img className="delete-icon" src="/images/trash-bin-black.svg" alt="delete" width={24} height={24}/>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
            )}
        </div>
      )}

      {/* Add question button */}
      {!showForm && (
        <button className="add-question-btn" onClick={() => setShowForm(true)}>
          + Add question
        </button>
      )}

      {/* Question form */}
      {showForm && (
        <div className="job-settings-form">
          <div className="job-settings-form-header">
            <select
              value={newQuestion.type}
              onChange={(e) =>
                setNewQuestion({ ...newQuestion, type: e.target.value })
              }
            >
              <option value="">Select question type</option>
              <option value="MCQ">MCQ</option>
              <option value="Numeric">Numeric</option>
              <option value="Yes/No">Yes or No</option>
              <option value="Free Text">Short answer</option>
            </select>
            <div className="job-settings-form-labels">
              <input 
                type="checkbox" 
                checked={!!newQuestion.is_required}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, is_required: e.target.checked })
                }
              />
              <label>Mandatory for qualification</label>
            </div>
            {/* <div className="job-settings-form-labels">
              <input type="checkbox" />
              <label>Auto reject if not qualified</label>
            </div> */}
          </div>
          <div className="job-settings-form-question">
              <label>Question</label>
              <input
                type="text"
                placeholder="Enter question"
                value={newQuestion.question}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, question: e.target.value })
                }
              />
          </div>
          {/* MCQ Answer choices */}
            {newQuestion.type === "MCQ" && (
              <div className="job-settings-form-choices">
                <label>Answer Choices</label>
                {newQuestion.choices.map((choice, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                    <input
                      type="text"
                      placeholder={`Option ${i + 1}`}
                      value={typeof choice === "object" ? choice.option_text : choice}
                      onChange={(e) => handleChoiceChange(e.target.value, i)}
                    />
                    {newQuestion.choices.length > 2 && (
                      <img
                        src="/images/cross-pink.svg"
                        alt="remove"
                        width={12}
                        height={12}
                        style={{ cursor: "pointer", marginLeft: "8px" }}
                        onClick={() => {
                          if (newQuestion.choices.length > 2) {
                            const updated = [...newQuestion.choices];
                            updated.splice(i, 1);
                            setNewQuestion({ ...newQuestion, choices: updated });
                          }
                        }}
                      />
                    )}
                  </div>
                ))}
                {newQuestion.choices.length < 4 && (
                  <button className="choice-btn" type="button" onClick={handleAddChoice}>
                    + Add Choice
                  </button>
                )}
              </div>
            )}
          <div className="job-settings-form-answer">
            <label>Suggested / Correct Answer</label>
            {newQuestion.type === "MCQ" && (
              <select
                value={newQuestion.correctAnswer}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, correctAnswer: e.target.value })
                }
              >
                <option value="">Select option</option>
                {newQuestion.choices.map((c, i) => {
                  const text = typeof c === "object" ? c.option_text : c;
                  return (
                    <option key={i} value={text}>
                      {text || `Option ${i + 1}`}
                    </option>
                  );
                })}
              </select>
            )}
            {newQuestion.type === "Numeric" && (
              <input
                type="number"
                min="0"
                max="100"
                value={newQuestion.correctAnswer}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, correctAnswer: e.target.value })
                }
              />
            )}
            {newQuestion.type === "Yes/No" && (
              <select
                value={newQuestion.correctAnswer}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, correctAnswer: e.target.value })
                }
              >
                <option value="">Select answer</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            )}
            {newQuestion.type === "Free Text" && (
              <div className="d-flex flex-column gap-2">
                <input
                  className="text-input"
                  type="text"
                  // maxLength={50}
                  placeholder="Enter suggested answer (max 50 characters)"
                  value={newQuestion.correctAnswer}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, correctAnswer: e.target.value })
                  }
                />
                {newQuestion.correctAnswer.length > 50 ? (
                  <small style={{ color: 'red' }}>
                    Please summarize your answer in 50 characters only
                  </small>
                ) : (
                  <small style={{ color: '#777' }}>
                    Please enter text in 50 characters only
                  </small>
                )}
              </div>
            )}
          </div>
          <div className="job-settings-form-score">
            <label>Score</label>
            <input
              type="number"
              value={newQuestion.score}
              min="0"
              max="100"
              onChange={(e) =>
                setNewQuestion({ ...newQuestion, score: e.target.value })
              }
            />
          </div>
          <div className="d-flex gap-4 align-items-center">
            <button className="save-btn" type="button" onClick={validateAndSave} disabled={saveLoading}>
              {saveLoading ? 'Saving...' : 'Save' }
            </button>
            <h6 className="cursor-pointer cancle-txt" onClick={() => {handleBack()}}>Cancel</h6>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <div className="job-settings-bottom">
        <p>Set Pre-Screening Pass Score</p>
        <input
          className="number-input"
          type="number"
          min="0"
          max={totalScore}
          value={passingScore}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value <= totalScore) {
              setPassingScore(value);
            }
          }}
        />
        {passingScore !== originalScore && (
          <button
            className="save-btn"
            type="button"
            onClick={handleSavePassingScore}
            disabled={saveLoading}
          >
            {saveLoading ? "Saving..." : "Save"}
          </button>
        )}
        <p style={{ fontSize: "12px", color: "#666" }}>
          Max allowed: {totalScore}
        </p>
      </div>
    </div>
  );
};

export default PreScreening;